import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FileCheck, ScrollText, Check, X, Flag } from "lucide-react";
import { api, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Page, PageTitle, Card, GoldButton, GhostButton, Loader, Empty } from "../components/UI";
import { StatusBadge } from "../components/Badges";
import { ClubCrest } from "../components/ClubCrest";
import { RosterCounter } from "../components/RosterCounter";
import { FederationTransfersPanel } from "../components/Transfers";

export default function FedAdminDashboard() {
  const { user, hasRole } = useAuth();
  const [pending, setPending] = useState(null);
  const [audit, setAudit] = useState([]);
  const [reasons, setReasons] = useState({});

  const load = useCallback(() => {
    api.get("/admin/federation/clubs/pending").then(({ data }) => setPending(data.clubs)).catch(() => setPending([]));
    api.get("/admin/audit?limit=30").then(({ data }) => setAudit(data.logs)).catch(() => setAudit([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (club, action) => {
    const reason = reasons[club.id] || (action === "approve" ? "Approved — roster and compliance verified." : "Rejected.");
    try {
      await api.post(`/admin/clubs/${club.id}/${action}`, { reason });
      toast.success(`Club ${action}d — ${club.wsbf_club_id}`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!hasRole("FEDERATION_ADMIN", "WSBF_ADMIN")) {
    return <Page><Empty label="Federation admin access required" /></Page>;
  }
  if (!pending) return <Loader />;

  return (
    <Page>
      <PageTitle
        eyebrow={hasRole("WSBF_ADMIN") ? "WSTF Administration" : "Hellenic Federation"}
        title="Federation Console"
        subtitle="Review club applications and audit federation activity."
      />

      <Link to="/admin/national-team" data-testid="nav-build-national-team"
        className="block rounded-2xl foil-border bg-gradient-to-r from-[#14110a] to-[#070707] p-5 mb-10 hover:border-[#E5B842]/60 group"
        style={{ transition: "border-color 0.15s ease" }}>
        <div className="flex items-center gap-4">
          <Flag className="w-8 h-8 text-[#E5B842]" />
          <div className="flex-1">
            <div className="font-heading text-xl font-black uppercase tracking-tight text-slate-50">Build National Team</div>
            <div className="text-sm text-slate-400">The system recommends; you select. Lock &amp; publish your 15–25 squad for the Nations Series.</div>
          </div>
          <span className="font-mono text-xs uppercase tracking-wider text-[#E5B842] group-hover:translate-x-1" style={{ transition: "transform 0.15s ease" }}>Open builder →</span>
        </div>
      </Link>

      <div className="mb-10"><FederationTransfersPanel /></div>
      <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-slate-50 mb-3 flex items-center gap-2"><FileCheck className="w-5 h-5 text-[#E5B842]" /> Pending Club Applications</h2>
      {pending.length === 0 ? (
        <Card className="p-6 text-slate-500 text-sm mb-10" data-testid="no-pending">No club applications awaiting review.</Card>
      ) : (
        <div className="space-y-4 mb-10" data-testid="pending-clubs">
          {pending.map((club) => (
            <Card key={club.id} className="p-5" data-testid={`pending-club-${club.wsbf_club_id}`}>
              <div className="flex items-start gap-4">
                <ClubCrest club={club} size={52} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Link to={`/club/${club.id}`} className="font-heading text-xl font-bold uppercase text-slate-50 hover:text-[#E5B842]" style={{ transition: "color 0.15s ease" }}>{club.name}</Link>
                    <StatusBadge status={club.status} />
                  </div>
                  <div className="font-mono text-[11px] text-[#E5B842] tracking-wider">{club.wsbf_club_id} · {club.city}</div>
                </div>
              </div>
              <div className="mt-4 grid md:grid-cols-2 gap-4">
                <RosterCounter roster={club.roster} />
                <div className="flex flex-col gap-2">
                  <input
                    data-testid={`reason-${club.wsbf_club_id}`}
                    value={reasons[club.id] || ""}
                    onChange={(e) => setReasons((r) => ({ ...r, [club.id]: e.target.value }))}
                    placeholder="Decision reason (logged to audit)"
                    className="w-full bg-[#000000] border border-[#2b2512] rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-[#E5B842]/50"
                  />
                  <div className="flex gap-2">
                    <GoldButton className="flex-1" data-testid={`approve-${club.wsbf_club_id}`} onClick={() => act(club, "approve")}><Check className="w-4 h-4" /> Approve</GoldButton>
                    <GhostButton className="flex-1" data-testid={`reject-${club.wsbf_club_id}`} onClick={() => act(club, "reject")}><X className="w-4 h-4" /> Reject</GhostButton>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-slate-50 mb-3 flex items-center gap-2"><ScrollText className="w-5 h-5 text-[#E5B842]" /> Audit Log</h2>
      <Card className="overflow-hidden" data-testid="audit-log">
        {audit.length === 0 ? (
          <div className="p-6 text-slate-500 text-sm">No audit entries.</div>
        ) : (
          audit.map((l) => (
            <div key={l.id} className="px-5 py-3 border-t border-[#2b2512]/60 first:border-t-0 flex items-center gap-3 text-sm">
              <span className="font-mono text-[11px] text-[#E5B842] uppercase w-44 shrink-0">{l.action}</span>
              <span className="text-slate-400 flex-1 truncate">{l.entity_type} · {l.new_state == null ? "—" : (typeof l.new_state === "string" ? l.new_state : JSON.stringify(l.new_state))}{l.reason ? ` · ${l.reason}` : ""}</span>
              <span className="font-mono text-[10px] text-slate-600 hidden sm:block">{(l.timestamp || "").slice(0, 19).replace("T", " ")}</span>
            </div>
          ))
        )}
      </Card>
    </Page>
  );
}
